// Auswertungen: Ausgaben, Vorrat, Verschwendung.
//
// Die Zahlen dafür fielen bisher schon an (Bon-Preise, wasted-Events) und
// wurden nur nie irgendwo gezeigt.
import { formatEuro, localDate } from '@speis/shared';
import type { SpendingSummary } from '@speis/shared';
import { api } from '../core/api.js';
import { guard } from '../core/guard.js';
import { barChart } from '../ui/chart.js';
import { clear, el, replace } from '../ui/dom.js';
import { openSheet } from '../ui/sheet.js';

interface PantryStats {
  products: number;
  inStock: number;
  belowMin: number;
  batches: number;
  expiringSoon: number;
  expired: number;
  estimatedValue: number;
}

interface WasteSummary {
  items: number;
  units: number;
  euros: number;
  top: { name: string; units: number; euros: number }[];
}

export async function openStats(): Promise<void> {
  const body = el(
    'div',
    {},
    el('div', { class: 'loading-block' }, el('span', { class: 'spinner' })),
  );
  openSheet(body, { title: 'Auswertungen' });

  const month = localDate().slice(0, 7);

  const [spending, pantry, waste] = await Promise.all([
    guard(
      () =>
        api<{ current: SpendingSummary; trend: { month: string; total: number }[] }>(
          `/api/stats/spending?month=${month}`,
        ),
      { failure: 'Ausgaben' },
    ),
    guard(() => api<PantryStats>('/api/stats/pantry'), { failure: 'Vorrat' }),
    guard(
      () =>
        api<{ month: WasteSummary; last90Days: WasteSummary }>(`/api/stats/waste?month=${month}`),
      { failure: 'Verschwendung' },
    ),
  ]);

  clear(body);

  if (pantry) body.append(pantrySection(pantry));
  if (spending) body.append(spendingSection(spending.current, spending.trend));
  if (waste) body.append(wasteSection(waste.month, waste.last90Days));

  if (!pantry && !spending && !waste) {
    body.append(el('p', { class: 'muted' }, 'Auswertungen sind offline nicht verfügbar.'));
  }
}

function pantrySection(stats: PantryStats): HTMLElement {
  return el('section', { class: 'screen-section' }, [
    el('h2', { class: 'settings-head' }, 'Vorrat'),
    el('div', { class: 'stat-grid' }, [
      stat(String(stats.inStock), `von ${stats.products} Produkten vorrätig`),
      stat(formatEuro(stats.estimatedValue), 'geschätzter Warenwert', 'stat-brand'),
      stats.belowMin ? stat(String(stats.belowMin), 'unter Meldebestand', 'stat-warn') : null,
      stats.expiringSoon ? stat(String(stats.expiringSoon), 'laufen bald ab', 'stat-warn') : null,
      stats.expired ? stat(String(stats.expired), 'abgelaufen', 'stat-danger') : null,
    ]),
    stats.estimatedValue === 0
      ? el(
          'p',
          { class: 'tiny' },
          'Der Warenwert entsteht aus gescannten Bons – nach dem ersten Bon-Scan steht hier eine Zahl.',
        )
      : null,
  ]);
}

function spendingSection(
  current: SpendingSummary,
  trend: { month: string; total: number }[],
): HTMLElement {
  const section = el('section', { class: 'screen-section' }, [
    el('h2', { class: 'settings-head' }, 'Ausgaben'),
  ]);

  if (!current.total && !trend.some((t) => t.total > 0)) {
    section.append(
      el(
        'p',
        { class: 'muted' },
        'Noch keine Bons erfasst. Scanne über den ＋-Knopf einen Kassenbon – daraus entstehen Preise, Ausgabenverlauf und die Schätzsumme auf der Einkaufsliste.',
      ),
    );
    return section;
  }

  section.append(
    el('div', { class: 'stat-grid' }, [
      stat(formatEuro(current.total), 'diesen Monat', 'stat-brand'),
      current.byStore[0] ? stat(current.byStore[0].store, 'häufigster Markt') : null,
    ]),
  );

  if (trend.length > 1) {
    section.append(
      el('div', { class: 'card' }, [
        el('div', { class: 'section-title' }, [el('span', {}, 'Monatsverlauf')]),
        barChart({
          bars: trend.map((t) => ({ label: t.month.slice(5), value: t.total })),
          formatValue: (v) => `${Math.round(v)} €`,
          label: 'Ausgaben pro Monat',
        }),
      ]),
    );
  }

  if (current.byCategory.length) {
    section.append(
      el('h3', { class: 'eyebrow' }, 'Nach Kategorie'),
      breakdown(current.byCategory.map((c) => ({ label: c.category, value: c.total }))),
    );
  }

  if (current.topItems.length) {
    section.append(
      el('h3', { class: 'eyebrow' }, 'Größte Posten'),
      breakdown(current.topItems.map((i) => ({ label: i.name, value: i.total }))),
    );
  }

  return section;
}

function wasteSection(month: WasteSummary, last90: WasteSummary): HTMLElement {
  const section = el('section', { class: 'screen-section' }, [
    el('h2', { class: 'settings-head' }, 'Weggeworfen'),
  ]);

  if (!last90.units) {
    section.append(
      el(
        'p',
        { class: 'notice' },
        'In den letzten 90 Tagen wurde nichts als weggeworfen markiert. Weiter so – und falls doch mal etwas schlecht wird: In „Läuft ab" auf „Weg" tippen, dann steht hier, was es gekostet hat.',
      ),
    );
    return section;
  }

  section.append(
    el('div', { class: 'stat-grid' }, [
      stat(formatEuro(month.euros), 'diesen Monat', 'stat-danger'),
      stat(String(month.units), 'Packungen'),
      stat(formatEuro(last90.euros), 'in 90 Tagen', 'stat-warn'),
    ]),
  );

  if (last90.top.length) {
    section.append(
      el('h3', { class: 'eyebrow' }, 'Am häufigsten betroffen'),
      breakdown(last90.top.map((t) => ({ label: `${t.name} (${t.units}×)`, value: t.euros }))),
      el(
        'p',
        { class: 'tiny' },
        'Tipp: Für diese Produkte lohnt sich ein kleinerer Meldebestand oder eine kleinere Packungsgröße.',
      ),
    );
  }

  return section;
}

function stat(value: string, label: string, cls = ''): HTMLElement {
  return el('div', { class: `stat ${cls}` }, [
    el('div', { class: 'stat-value' }, value),
    el('div', { class: 'stat-label' }, label),
  ]);
}

/** Einfache Balkenliste – gut lesbar auch bei vielen Zeilen. */
function breakdown(rows: { label: string; value: number }[]): HTMLElement {
  const max = Math.max(...rows.map((r) => r.value), 0.01);
  const wrap = el('div', { class: 'breakdown' });
  for (const row of rows) {
    wrap.append(
      el('div', { class: 'breakdown-row' }, [
        el('div', { class: 'breakdown-head' }, [
          el('span', {}, row.label),
          el('span', { class: 'tabular' }, formatEuro(row.value)),
        ]),
        el('div', { class: 'breakdown-track' }, [
          el('div', {
            class: 'breakdown-fill',
            style: `width:${Math.max(2, (row.value / max) * 100)}%`,
          }),
        ]),
      ]),
    );
  }
  return wrap;
}

export { replace };
